/**
 * Seating someone by tapping — the way to do on a phone what a drag does with
 * a mouse, and it works with a mouse too.
 *
 * The planner picks a guest (from the list, or "Move to another chair" on a
 * seated one), the editor waits, and the next chair tapped gets them — or, for
 * a table tapped as a whole, its first free chair, because at a phone's zoom a
 * chair is a few pixels wide and the table is the target a thumb can hit.
 * Which chair and who swaps is the store's business (`seatGuest`); this decides
 * where the planner lands afterwards.
 */

import type { Catalogue } from './i18n'
import { type SeatRef, usePlanStore } from '../store/planStore'
import { useViewStore } from '../store/viewStore'

export function finishPlacing(target: { tableId: string; seatId?: string }, t: Catalogue): void {
  const view = useViewStore.getState()
  const placing = view.placing
  if (!placing) return
  if (placing.kind === 'swap') {
    finishSwap(placing.tableId, target.tableId, t)
    return
  }

  const store = usePlanStore.getState()
  const table = store.plan?.tables.find((entry) => entry.id === target.tableId)
  const guest = store.plan?.guests.find((entry) => entry.id === placing.guestId)
  if (!table || !guest) {
    view.stopPlacing()
    return
  }

  let seat: SeatRef | null
  if (target.seatId) {
    seat = { tableId: table.id, seatId: target.seatId }
    store.seatGuest(guest.id, seat)
  } else {
    seat = store.seatGuestAtTable(guest.id, table.id)
    if (!seat) {
      // Still waiting: another table is one tap away.
      store.showToast(t.placing.tableFull(table.name), 'warn')
      return
    }
  }

  const position = table.seats.find((entry) => entry.id === seat.seatId)?.position ?? 0
  view.stopPlacing()
  // Someone being *moved* was being looked at, so follow them to the new chair;
  // someone picked from the list goes back to the list for the next name.
  if (placing.returnTo === 'selection' || !view.compact) store.setActiveSeat(seat)
  store.showToast(`${guest.fullName} — ${t.rail.seatAt(table.name, position + 1)}`, 'success')
}

/**
 * A table's guests trading places with the tapped table's — a chair counts as
 * its table, since a thumb aiming at a table often lands on one. Tapping the
 * table the swap started from is the other way out besides Cancel.
 */
function finishSwap(sourceId: string, targetId: string, t: Catalogue): void {
  useViewStore.getState().stopPlacing()
  if (sourceId !== targetId) swapTables(sourceId, targetId, t)
}

/**
 * Trades two tables' guests and says what happened: a move when one side was
 * empty, a swap otherwise, and a warning for anyone who ended up on the list.
 */
export function swapTables(aId: string, bId: string, t: Catalogue): void {
  const store = usePlanStore.getState()
  const a = store.plan?.tables.find((entry) => entry.id === aId)
  const b = store.plan?.tables.find((entry) => entry.id === bId)
  const result = a && b ? store.swapTableGuests(aId, bId) : null
  if (!a || !b || !result) return

  const { toA, toB, unseated } = result
  if (toA + toB + unseated === 0) {
    store.showToast(t.swap.nothing, 'info')
    return
  }
  let message =
    toA === 0
      ? t.swap.moved(toB, a.name, b.name)
      : toB === 0
        ? t.swap.moved(toA, b.name, a.name)
        : t.swap.swapped(a.name, b.name)
  if (unseated > 0) message = `${message} ${t.swap.overflow(unseated)}`
  store.showToast(message, unseated > 0 ? 'warn' : 'success')
}
